import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'manager', label: 'Manager' },
];

const UserAdmin = () => {
  const { user: currentUser, fetchUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: 'customer',
    is_active: true,
    is_staff: false,
    is_superuser: false,
  });
  const [saving, setSaving] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);

  const loadUsers = async () => {
    setError('');
    try {
      const res = await api.get('/auth/admin/users/');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      role: u.role,
      is_active: u.is_active,
      is_staff: !!u.is_staff,
      is_superuser: !!u.is_superuser,
    });
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/auth/admin/users/${editUser.id}/`, editForm);
      await loadUsers();
      if (editUser.id === currentUser?.id) {
        await fetchUser();
      }
      setEditUser(null);
    } catch (e) {
      const d = e.response?.data;
      setError(typeof d === 'object' ? JSON.stringify(d) : d || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/auth/admin/users/${deleteUser.id}/`);
      await loadUsers();
      setDeleteUser(null);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
          Portal users (superuser)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          View all accounts, adjust primary role and flags, or delete users. Profile links (customer /
          driver / manager) are unchanged; accessible portals still follow those records.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Typography>Loading…</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Primary role</TableCell>
                    <TableCell>Portals</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Superuser</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(u.accessible_roles || []).map((r) => (
                            <Chip key={r} label={r} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{u.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.is_staff ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.is_superuser ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(u)} aria-label="edit user">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={u.id === currentUser?.id}
                              onClick={() => setDeleteUser(u)}
                              aria-label="delete user"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog open={!!editUser} onClose={() => !saving && setEditUser(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit user</DialogTitle>
          <DialogContent>
            {editUser && (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {editUser.email}
                </Typography>
                <TextField
                  select
                  label="Primary role (User.role)"
                  value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  fullWidth
                >
                  {ROLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                  }
                  label="Active (can log in)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_staff}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_staff: e.target.checked }))}
                    />
                  }
                  label="Staff (Django admin access)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_superuser}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_superuser: e.target.checked }))}
                    />
                  }
                  label="Superuser"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteUser} onClose={() => !saving && setDeleteUser(null)}>
          <DialogTitle>Delete user?</DialogTitle>
          <DialogContent>
            <Typography>
              Permanently delete <strong>{deleteUser?.email}</strong>? Related profiles may be removed by
              the database (cascade).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default UserAdmin;
